/**
 * React Hook Form + Zod thin integration layer.
 *
 * Provides `useZodForm` (pre-wired useForm with Zod resolver) with a
 * `registerPluma` function that returns Pluma-compatible field props.
 *
 * All standard RHF primitives (Controller, useFormContext, FormProvider)
 * are re-exported for convenience.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { get } from 'lodash-es';
import { useForm, useFormContext, useController, Controller, FormProvider as RHFFormProvider } from 'react-hook-form';
import { createContext, use, type ReactNode } from 'react';
import type { RefCallback } from 'react';
import type { FieldError } from 'react-hook-form';
import type { UseFormProps, UseFormReturn, FieldValues, FieldPath, FieldPathValue } from 'react-hook-form';

import type { z } from 'zod';

// ---------------------------------------------------------------------------
// Pluma field props — what registerPluma() returns
// ---------------------------------------------------------------------------

export type BaseFormProps<TInputType extends FieldValues, TOutputType extends FieldValues> = {
  onSubmit: (data: TOutputType, formContext: UseFormReturn<TInputType, unknown, TOutputType>) => Promise<void>;
  defaultValues: TInputType;
};

interface PlumaFieldProps<TValue> {
  /** Pluma `onInput` handler — fires on every keystroke for text inputs. */
  onInput: (event: { target: { value: string } }) => void;
  /** Pluma `onBlur` handler. */
  onBlur: () => void;
  /** Current field value, typed to match the schema field. */
  value: TValue;
  /** Field name. */
  name: string;
  /** Ref for RHF focus management (shouldFocusError). */
  ref: RefCallback<HTMLElement>;
  /** First error message, if any. */
  error: string | undefined;
}

// ---------------------------------------------------------------------------
// useZodForm — pre-configured useForm with Zod resolver + registerPluma
// ---------------------------------------------------------------------------

type UseZodFormProps<TFieldValues extends FieldValues> = Omit<UseFormProps<TFieldValues>, 'resolver'> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod v4 internals require `any` for the second type param
  schema: z.ZodType<TFieldValues, any>;
};

type UseZodFormReturn<TFieldValues extends FieldValues> = UseFormReturn<TFieldValues> & {
  registerPluma: <TName extends FieldPath<TFieldValues>>(
    name: TName,
  ) => PlumaFieldProps<FieldPathValue<TFieldValues, TName>>;
};

/**
 * A thin wrapper around `useForm` that wires up the Zod resolver and adds `registerPluma`.
 *
 * `registerPluma` works like RHF's `register()` but returns Pluma-compatible props
 * (onInput, value, error) instead of React-compatible ones (onChange, ref).
 *
 * RHF's `register()` doesn't work with Pluma because:
 * 1. Pluma components use `onInput` for keystrokes (not React's `onChange`)
 * 2. Pluma's `useImperativeHandle` ref chain breaks RHF's native event listener attachment
 *
 * ```tsx
 * const form = useZodForm({ schema, defaultValues: { email: '' } });
 *
 * <TextField {...form.registerPluma('email')} label="Email" />
 * ```
 */
export function useZodForm<TFieldValues extends FieldValues>({
  schema,
  ...rest
}: UseZodFormProps<TFieldValues>): UseZodFormReturn<TFieldValues> {
  const form = useForm<TFieldValues>({
    ...rest,
    resolver: zodResolver(schema),
  });

  // Attach registerPluma directly to the form object to keep referential stability.
  // Spreading ({ ...form, registerPluma }) would create a new object every render,
  // making `form` unsafe in useEffect dependency arrays.
  const formWithPluma = form as UseZodFormReturn<TFieldValues>;
  formWithPluma.registerPluma = <TName extends FieldPath<TFieldValues>>(
    name: TName,
  ): PlumaFieldProps<FieldPathValue<TFieldValues, TName>> => {
    const value = form.watch(name);
    const error = get(form.formState.errors, name) as FieldError | undefined;

    return {
      value,
      name,
      ref: (element) => {
        form.register(name).ref(element);
      },
      onInput: (event) => {
        void form.setValue(name, event.target.value as FieldPathValue<TFieldValues, TName>, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      },
      onBlur: () => {
        // Mark field as touched (for mode: 'onTouched' and touchedFields)
        void form.setValue(name, form.getValues(name), { shouldTouch: true });
        void form.trigger(name);
      },
      error: (error?.message as string) ?? undefined,
    };
  };

  return formWithPluma;
}

// ---------------------------------------------------------------------------
// FormProvider — wraps RHF's FormProvider and passes registerPluma via context
// ---------------------------------------------------------------------------

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- context needs to accept any form
const RegisterPlumaContext = createContext<UseZodFormReturn<any>['registerPluma'] | null>(null);

/**
 * FormProvider that passes both RHF form methods and registerPluma to children.
 * Use with useZodForm's return value.
 */
export function FormProvider<TFieldValues extends FieldValues>({
  children,
  ...form
}: UseZodFormReturn<TFieldValues> & { children: ReactNode }) {
  return (
    <RHFFormProvider {...form}>
      <RegisterPlumaContext.Provider value={form.registerPluma}>{children}</RegisterPlumaContext.Provider>
    </RHFFormProvider>
  );
}

/**
 * Access the form context including registerPluma in child components.
 */
export function usePlumaFormContext<TFieldValues extends FieldValues = FieldValues>() {
  const rhfContext = useFormContext<TFieldValues>();
  const registerPluma = use(RegisterPlumaContext);

  if (registerPluma == null) {
    throw new Error('usePlumaFormContext must be used within a FormProvider from @/components/form');
  }

  return {
    ...rhfContext,
    registerPluma: registerPluma as UseZodFormReturn<TFieldValues>['registerPluma'],
  };
}

// ---------------------------------------------------------------------------
// plumaFieldProps — standalone version of registerPluma for non-Zod forms
// ---------------------------------------------------------------------------

/**
 * Returns Pluma-compatible field props from any RHF form instance.
 * Use this when you can't use `useZodForm` (e.g. custom resolver).
 *
 * Uses `form.watch` internally so the calling component re-renders when the
 * field value changes — even when the form was created in a parent component
 * or provided via context.
 *
 * Note: unlike `registerPluma`, this does not pass a `ref` to RHF because
 * `form.register()` attaches native onChange listeners that conflict with
 * our onInput approach for `type="number"` inputs. This means `shouldFocusError`
 * won't auto-focus invalid fields.
 *
 * ```tsx
 * const form = useForm({ resolver: myCustomResolver, defaultValues: { name: '' } });
 * <TextField {...plumaFieldProps(form, 'name')} label="Name" />
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- needs to work with any form shape
export function plumaFieldProps(form: UseFormReturn<any>, name: string) {
  const value = form.watch(name);
  const error = get(form.formState.errors, name) as FieldError | undefined;

  return {
    value: value != null ? String(value) : '',
    name,
    onInput: (event: { target: { value: string } }) => {
      form.setValue(name, event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    onBlur: () => {
      form.setValue(name, form.getValues(name), { shouldTouch: true });
      void form.trigger(name);
    },
    error: (error?.message as string) ?? undefined,
  };
}

// Re-export RHF primitives
// fallow-ignore-next-line unused-export
export { useForm, useFormContext, useController, Controller };
export type { UseFormReturn } from 'react-hook-form';
